import { Spacing, Typography } from '@persona-id/design-tokens';
import { FC, useState } from 'react';
import { COLORS, SPACINGS } from 'shared/styles/variables';
import styled from 'styled-components';

import { PAGE_LIMIT_BEFORE_SCROLL_MODE } from 'dashboard/components/Pagination/PageSelector';
import Button from 'design-system/components/Button';
import { Input } from 'design-system/components/Input';
import { PaginationParams } from 'lib/interfaces/api';

const MAX_PAGES = 7;

interface PageLinkProps {
  currentPage: number;
  linkPage: number;
}

const PageSelector = styled('div')`
  display: flex;
  align-items: center;
  gap: 8px;
  color: ${COLORS.gray100};
`;

const PageLink = styled('div')<PageLinkProps>`
  padding: 0 ${SPACINGS.base}px;
  border-radius: 2px;
  cursor: pointer;

  ${({ currentPage, linkPage }) => {
    if (currentPage === linkPage) {
      return `
        ${Typography.TextMediumBold}
      `;
    }
  }};

  &:hover {
    background-color: ${COLORS.indigo20};
  }
`;

const Ellipsis = styled('div')`
  padding: 0 ${SPACINGS.base}px;
`;

const StyledNumInput = styled(Input)`
  width: 34px;
  text-align: left;
  padding: ${Spacing[1]};
  padding-left: ${Spacing[2]};
  margin-left: ${Spacing[2]};
  flex-shrink: 0;
  &:not(:last-child) {
    margin-right: ${Spacing[1]};
  }
`;

const PageInput = styled('div')`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const StyledInfoText = styled('div')`
  width: max-content;
`;

interface PageSelectorProps {
  paginationParams: PaginationParams;
  handlePageLinkClick: (pageNumber: number) => void;
}

const PageSelectorV2: FC<PageSelectorProps> = ({ paginationParams, handlePageLinkClick }) => {
  const [currentPage, setCurrentPage] = useState(paginationParams['current-page']);

  if (!paginationParams) {
    return <div />;
  }

  const { 'total-pages': totalPages } = paginationParams;

  const middleIndex = Math.floor(MAX_PAGES / 2);

  const pageLinks = [];

  if (totalPages <= MAX_PAGES) {
    // total number of pages is less than MAX_PAGES, so display all pages
    for (let i = 0; i < totalPages; ++i) {
      const pageNumber = i + 1;
      pageLinks.push(
        <PageLink
          currentPage={currentPage}
          linkPage={pageNumber}
          key={pageNumber}
          onClick={() => handlePageLinkClick(pageNumber)}
          data-test={`pagination__page-selector-${pageNumber}`}
          aria-current={currentPage === pageNumber}
          aria-label={`Go to page ${i} out of ${totalPages}`}
        >
          {pageNumber}
        </PageLink>,
      );
    }
  } else if (totalPages < PAGE_LIMIT_BEFORE_SCROLL_MODE) {
    if (currentPage <= middleIndex + 1) {
      // current page is close to the start, so only display end ellipsis
      // e.g. < 1 2 *3* 4 5 ... 9 >
      for (let i = 0; i <= middleIndex + 1; ++i) {
        const pageNumber = i + 1;
        pageLinks.push(
          <PageLink
            currentPage={currentPage}
            linkPage={pageNumber}
            key={pageNumber}
            onClick={() => handlePageLinkClick(pageNumber)}
            data-test={`pagination__page-selector-${pageNumber}`}
            aria-selected={currentPage === pageNumber}
          >
            {pageNumber}
          </PageLink>,
        );
      }

      pageLinks.push(<Ellipsis key='ellipsis_end'>...</Ellipsis>);

      pageLinks.push(
        <PageLink
          currentPage={currentPage}
          linkPage={totalPages}
          key={totalPages}
          onClick={() => handlePageLinkClick(totalPages)}
          data-test={`pagination__page-selector-${totalPages}`}
          aria-selected={currentPage === totalPages}
        >
          {totalPages}
        </PageLink>,
      );
    } else if (currentPage >= totalPages - middleIndex) {
      // current page is close to the end, so only display start ellipsis
      // e.g. < 1 ... 5 *6* 7 8 9 >
      pageLinks.push(
        <PageLink
          currentPage={currentPage}
          linkPage={1}
          key={1}
          onClick={() => handlePageLinkClick(1)}
          data-test='pagination__page-selector-1'
          aria-selected={currentPage === 1}
        >
          {1}
        </PageLink>,
      );

      pageLinks.push(<Ellipsis key='ellipsis_start'>...</Ellipsis>);

      for (let i = totalPages - middleIndex - 1; i <= totalPages; ++i) {
        const pageNumber = i;
        pageLinks.push(
          <PageLink
            currentPage={currentPage}
            linkPage={pageNumber}
            key={pageNumber}
            onClick={() => handlePageLinkClick(pageNumber)}
            data-test={`pagination__page-selector-${pageNumber}`}
            aria-selected={currentPage === pageNumber}
          >
            {pageNumber}
          </PageLink>,
        );
      }
    } else {
      // current page is in the middle, so display double ellipsis
      // e.g. < 1 ... 4 *5* 6 ... 9 >
      pageLinks.push(
        <PageLink
          currentPage={currentPage}
          linkPage={1}
          key={1}
          onClick={() => handlePageLinkClick(1)}
          data-test='pagination__page-selector-1'
          aria-selected={currentPage === 1}
        >
          {1}
        </PageLink>,
      );

      pageLinks.push(<Ellipsis key='ellipsis_start'>...</Ellipsis>);

      for (let i = currentPage - 1; i <= currentPage + 1; ++i) {
        const pageNumber = i;
        pageLinks.push(
          <PageLink
            currentPage={currentPage}
            linkPage={pageNumber}
            key={pageNumber}
            onClick={() => handlePageLinkClick(pageNumber)}
            data-test={`pagination__page-selector-${pageNumber}`}
            aria-selected={currentPage === pageNumber}
          >
            {pageNumber}
          </PageLink>,
        );
      }

      pageLinks.push(<Ellipsis key='ellipsis_end'>...</Ellipsis>);

      pageLinks.push(
        <PageLink
          currentPage={currentPage}
          linkPage={totalPages}
          key={totalPages}
          onClick={() => handlePageLinkClick(totalPages)}
          data-test={`pagination__page-selector-${totalPages}`}
          aria-selected={currentPage === totalPages}
        >
          {totalPages}
        </PageLink>,
      );
    }
  } else {
    // Scroll mode.
    const HALF_MAX_PAGES = Math.floor(MAX_PAGES / 2);

    if (currentPage < HALF_MAX_PAGES + 1) {
      // currentPage is one of the first. Show
      // *1* 2 3 4 5 6 7
      // 1 *2* 3 4 5 6 7
      // 1 2 *3* 4 5 6 7
      for (let i = 1; i <= MAX_PAGES; ++i) {
        const pageNumber = i;
        pageLinks.push(
          <PageLink
            currentPage={currentPage}
            linkPage={pageNumber}
            key={pageNumber}
            onClick={() => handlePageLinkClick(pageNumber)}
            data-test={`pagination__page-selector-${pageNumber}`}
            aria-selected={currentPage === pageNumber}
          >
            {pageNumber}
          </PageLink>,
        );
      }
    } else if (currentPage > totalPages - HALF_MAX_PAGES) {
      // currentPage is one of the last. Show
      // 1 2 3 4 5 6 *7*
      // 1 2 3 4 5 *6* 7
      // 1 2 3 4 *5* 6 7
      for (let i = totalPages - 7; i <= totalPages; ++i) {
        const pageNumber = i;
        pageLinks.push(
          <PageLink
            currentPage={currentPage}
            linkPage={pageNumber}
            key={pageNumber}
            onClick={() => handlePageLinkClick(pageNumber)}
            data-test={`pagination__page-selector-${pageNumber}`}
            aria-selected={currentPage === pageNumber}
          >
            {pageNumber}
          </PageLink>,
        );
      }
    } else {
      // currentPage is in the middle. Always center the current page
      for (let i = currentPage - HALF_MAX_PAGES; i <= currentPage + HALF_MAX_PAGES; ++i) {
        const pageNumber = i;
        pageLinks.push(
          <PageLink
            currentPage={currentPage}
            linkPage={pageNumber}
            key={pageNumber}
            onClick={() => handlePageLinkClick(pageNumber)}
            data-test={`pagination__page-selector-${pageNumber}`}
            aria-selected={currentPage === pageNumber}
          >
            {pageNumber}
          </PageLink>,
        );
      }
    }
  }

  return (
    <PageSelector data-test='pagination__page-selector' id='pagination__page-selector'>
      <PageInput>
        <StyledNumInput
          size='sm'
          id='pagination__page-selector--input'
          min={1}
          max={totalPages}
          value={currentPage}
          onChange={(e) => {
            const pageNumber = parseInt(e.currentTarget.value);
            if (pageNumber > 0 && pageNumber <= totalPages) {
              setCurrentPage(pageNumber);
            }
          }}
          onKeyUp={(e) => {
            if (e.key === 'Enter') {
              const pageNumber = parseInt(e.currentTarget.value);
              if (pageNumber > 0 && pageNumber <= totalPages) {
                handlePageLinkClick(pageNumber);
              }
            }
          }}
        />
        <StyledInfoText>{`of ${totalPages} pages`}</StyledInfoText>
      </PageInput>
      <Button
        size='sm'
        variant='basic'
        id='pagination__page-selector--left'
        disabled={currentPage === 1}
        onClick={() => currentPage !== 1 && handlePageLinkClick(currentPage - 1)}
        data-test='pagination__page-selector-paddle-left'
      >
        Previous
      </Button>
      <Button
        type='button'
        size='sm'
        variant='inverse'
        id='pagination__page-selector--right'
        disabled={currentPage === totalPages}
        onClick={() => currentPage !== totalPages && handlePageLinkClick(currentPage + 1)}
        data-test='pagination__page-selector-paddle-right'
      >
        Next
      </Button>
    </PageSelector>
  );
};

export default PageSelectorV2;
